import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { track } from "@/lib/analytics"
import { Loader2 } from "lucide-react"

export default function ListingTeaserStep({
  onNext,
  onSkip,
}: {
  onNext: (url: string) => void
  onSkip: () => void
}) {
  const [url, setUrl] = useState("")
  const [importing, setImporting] = useState(false)

  function handleImport() {
    if (!url) return
    setImporting(true)
    track("onboarding_listing_imported")
    onNext(url)
  }

  return (
    <div className="text-center">
      <h2 className="font-display text-3xl font-semibold tracking-tight text-charcoal sm:text-4xl">
        Got a listing ready?
      </h2>
      <p className="mx-auto mt-3 max-w-md text-warm-grey">
        Paste a property listing URL and we&apos;ll import the photos, details,
        and generate everything you need. We&apos;ll take you straight to it.
      </p>

      <div className="mx-auto mt-10 max-w-md text-left">
        <Label htmlFor="listing_url">Listing URL</Label>
        <Input
          id="listing_url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.rightmove.co.uk/properties/..."
          className="mt-1.5"
          disabled={importing}
        />
        <p className="mt-2 text-xs text-warm-grey">
          Rightmove, Zoopla, OnTheMarket, Airbnb: we handle them all.
        </p>
      </div>

      {/* Preview teaser */}
      <div className="mx-auto mt-8 max-w-md overflow-hidden rounded-2xl border border-stone/40 bg-white">
        <div className="relative bg-gradient-to-br from-charcoal to-slate p-8">
          <div className="mx-auto flex aspect-video max-w-xs items-center justify-center rounded-lg border border-white/10 bg-white/5">
            <div className="text-center">
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-red/20">
                <svg className="size-5 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                </svg>
              </div>
              <p className="text-xs font-medium text-white/70">
                Your video preview
              </p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm font-medium text-charcoal">
            Paste a URL above to see what happens next
          </p>
          <p className="mt-1 text-xs text-warm-grey">
            Photos, details, and a script, all generated automatically
          </p>
        </div>
      </div>

      <div className="mt-10 flex flex-col items-center gap-3">
        <Button
          onClick={handleImport}
          disabled={!url || importing}
          className="bg-red px-10 py-5 text-sm font-semibold text-white hover:bg-red-deep disabled:opacity-40"
        >
          {importing ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Importing listing…
            </>
          ) : (
            "Import listing"
          )}
        </Button>
        {!importing && (
          <button
            type="button"
            onClick={() => {
              track("onboarding_listing_skipped")
              onSkip()
            }}
            className="text-sm text-warm-grey underline-offset-4 hover:text-charcoal hover:underline"
          >
            Skip for now
          </button>
        )}
      </div>
    </div>
  )
}
